import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '~/lib/utils';

const badgeVariants = cva(
  `
    inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden
    rounded-sm border px-sm py-xs text-body-small font-medium whitespace-nowrap
    transition-[color,box-shadow]
    focus-visible:border-ring focus-visible:ring-[3px]
    focus-visible:ring-ring/50
    aria-invalid:border-destructive aria-invalid:ring-destructive/20
    dark:aria-invalid:ring-destructive/40
    [&>svg]:pointer-events-none [&>svg]:size-3
  `,
  {
    variants: {
      variant: {
        default: `
          border-transparent bg-primary text-primary-foreground!
          [a&]:hover:bg-primary/90
        `,
        secondary: `
          border-transparent bg-secondary text-secondary-foreground
          [a&]:hover:bg-secondary/90
        `,
        destructive: `
          border-transparent bg-destructive text-white
          focus-visible:ring-destructive/20
          dark:bg-destructive/60
          dark:focus-visible:ring-destructive/40
          [a&]:hover:bg-destructive/90
        `,
        outline: `
          text-foreground
          [a&]:hover:bg-accent [a&]:hover:text-accent-foreground
        `,
        // Custom Figma-inspired variants using CSS variables from app.css
        grey: 'border-transparent bg-background-100 text-text-700!',
        green: 'border-transparent bg-green-50 text-green-700!',
        red: 'border-transparent bg-red-50 text-red-700!',
        yellow: 'border-transparent bg-yellow-50 text-yellow-700!',
        blue: 'border-transparent bg-blue-50 text-blue-700!',
      },
      size: {
        large: 'px-sm py-xs text-body-small',
        small: 'px-xs py-0 text-body-caption',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'large',
    },
  }
);

function Badge({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp
      data-slot="badge"
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
